import React, { useState } from 'react';
import './AnnotationGuide.css';

function LabelExample({ title, definition, examples, rules, img, imgsMeta, id }) {
  const [index, setIndex] = useState(0);

  const items = imgsMeta && imgsMeta.length > 0 ? imgsMeta : (img ? [{ src: img, caption: title }] : []);

  function prev() { setIndex((index - 1 + items.length) % items.length); }
  function next() { setIndex((index + 1) % items.length); }

  // Extract label ID if present (e.g., "Label 0")
  const labelIdMatch = title.match(/Label \d+/);
  const displayTitle = labelIdMatch ? title.replace(`${labelIdMatch[0]}: `, "") : title;
  const labelTag = labelIdMatch ? labelIdMatch[0] : null;

  return (
    <section className="label-section" id={id}>
      <div className="label-header">
        <div className="label-meta">
          {labelTag && <span className="label-badge">{labelTag}</span>}
          <h2 className="label-title">{displayTitle}</h2>
        </div>
        <p className="label-definition">
          <span className="label-keyword">Definition:</span> {definition}
        </p>
      </div>

      <div className="label-content">
        {items.length > 0 && (
          <div className="figure-container">
            <div className="figure-viewport">
              {items.length > 1 && (
                <button className="nav-btn prev" onClick={prev} aria-label="Previous">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
              )}
              <div className="image-wrapper">
                <img className="figure-image" src={items[index].src} alt={items[index].caption || title} />
              </div>
              {items.length > 1 && (
                <button className="nav-btn next" onClick={next} aria-label="Next">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              )}
            </div>
            <figcaption className="figure-caption">
              <span className="figure-number">FIG. {id.replace('label', '').toUpperCase()}</span>
              <span className="figure-sep"> &mdash; </span>
              <span className="figure-text">{items[index].caption || displayTitle}</span>
              {items[index].note && <span className="figure-note"> ({items[index].note})</span>}
              {items.length > 1 && <span className="figure-count"> ({index + 1} of {items.length})</span>}
            </figcaption>
          </div>
        )}

        <div className="label-guidelines">
          <div className="guideline-group">
            <h3 className="guideline-title">Examples</h3>
            <ul className="guideline-list">
              {examples.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
          <div className="guideline-group">
            <h3 className="guideline-title">Annotation Rules</h3>
            <ul className="guideline-list">
              {rules.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function AnnotationGuide() {
  const publicRoot = '/static/images/annotation_examples';
  const logoRoot = '/static/images/logo';

  return (
    <div className="annotation-guide-wrapper docs-page-section">
      <article className="paper-container">
        <header className="paper-header">
          <div className="paper-meta">IPO-MINE DATASET ANNOTATION PROTOCOL</div>
          <h1 className="paper-title">Annotation Guide</h1>
          <div className="paper-abstract">
            <p>
              This guide outlines the classification scheme used for the IPO-Mine multimodal dataset. 
              Annotators were instructed to select the single most appropriate label describing the dominant content of each image.
            </p>
            <div className="paper-divider"></div>
            <p>During the annotation phase, images were labeled using a fine-grained classification scheme. These labels were later mapped to the consolidated set of classes defined below to ensure maximum consistency and analytical utility.</p>
            <p>Ambiguity is inherent in some SEC filing imagery (e.g., text-heavy infographics). In such cases, annotators identified the dominant visual function. The classes below represent the finalized taxonomy used for our benchmarks and analysis.</p>
          </div>
        </header>

        <main className="paper-body">
          <LabelExample
            id="label0"
            title="Label 0: Chart"
            definition="Visual representations of quantitative or statistical data."
            examples={[
              'Bar, line, pie, or area charts',
              'Scatter plots or box plots',
              'Charts with axes, legends, or numeric scales'
            ]}
            rules={[
              'Must display explicit quantitative values or trends',
              'Axes, legends, or numeric labels are strong indicators',
              'If embedded inside a table, label as Table'
            ]}
            imgsMeta={[
              { src: `${publicRoot}/image-1770366505730.png`, caption: 'Stacked bar chart' },
              { src: `${publicRoot}/image-1770366518853.png`, caption: 'Grouped bar chart' },
              { src: `${publicRoot}/image-1770366532801.png`, caption: 'Bar chart with annotations' },
              { src: `${publicRoot}/image-1770366632916.png`, caption: 'Bar chart (misleading)', note: 'Highly misleading — visually confusing or mislabeled' },
              { src: `${publicRoot}/image-1770366770532.png`, caption: 'Multi-panel figure (mixed chart types)' },
              { src: `${publicRoot}/image-1770366792119.png`, caption: 'Bar chart with map background', note: 'Contains a map as background element' }
            ]}
          />

          <LabelExample
            id="label1"
            title="Label 1: Infographic"
            definition="Composite visuals combining text, icons, and graphics to explain concepts or processes."
            examples={[
              'Business model diagrams',
              'Process flows with arrows',
              'Highlight summaries in S-1 filings'
            ]}
            rules={[
              'Emphasizes explanatory structure',
              'Lacks strict numeric axes or grids',
              'Minor charts may appear but are not dominant'
            ]}
            imgsMeta={[
              { src: `${publicRoot}/infograph/image-1770368095699.png`, caption: 'Infographic 1' },
              { src: `${publicRoot}/infograph/image-1770368108187.png`, caption: 'Infographic 2' },
              { src: `${publicRoot}/infograph/image-1770368144338.png`, caption: 'Infographic 3' },
              { src: `${publicRoot}/infograph/image-1770368158740.png`, caption: 'Infographic 4' },
              { src: `${publicRoot}/infograph/image-1770368803905.png`, caption: 'Infographic 5' }
            ]}
          />

          <LabelExample
            id="label2"
            title="Label 2: Logo"
            definition="Brand identifiers or symbolic marks representing a company, product, or organization."
            examples={[
              'Company or subsidiary logos',
              'Brand marks, emblems, or logotypes',
              'Stylized wordmarks or icons used for branding'
            ]}
            rules={[
              'Use when the image’s sole purpose is identification or branding',
              'Do not include photographs unless clearly stylized as a logo',
              'Exclude logos embedded within larger graphics'
            ]}
            imgsMeta={[
              { src: `${logoRoot}/image-1770369024311.png`, caption: 'Logo 1' },
              { src: `${logoRoot}/image-1770369048010.png`, caption: 'Logo 2' },
              { src: `${logoRoot}/image-1770369107627.png`, caption: 'Logo 3' },
              { src: `${logoRoot}/image-1770369141075.png`, caption: 'Logo 4' },
              { src: `${logoRoot}/image-1770369189312.png`, caption: 'Logo 5' },
              { src: `${logoRoot}/image-1770369210209.png`, caption: 'Logo 6' }
            ]}
          />

          <LabelExample
            id="label3"
            title="Label 3: Map"
            definition="Geographic or spatial representations emphasizing location or coverage."
            examples={[
              'Regional or global maps',
              'Operational coverage maps'
            ]}
            rules={[
              'Geography must be central',
              'Exclude decorative background maps',
              'If explanatory flow dominates, consider Infographic'
            ]}
            imgsMeta={[
              { src: `${publicRoot}/map/image-1770403958923.png`, caption: 'Map 1' },
              { src: `${publicRoot}/map/image-1770403972060.png`, caption: 'Map 2' },
              { src: `${publicRoot}/map/image-1770403991483.png`, caption: 'Map 3' },
              { src: `${publicRoot}/map/image-1770403999372.png`, caption: 'Map 4' },
              { src: `${publicRoot}/map/image-1770404008249.png`, caption: 'Map 5' }
            ]}
          />

          <section className="label-section consolidated-section" id="label4">
            <div className="label-header">
              <div className="label-meta">
                <span className="label-badge">Label 4</span>
                <h2 className="label-title">Other</h2>
              </div>
              <p className="label-definition">
                <span className="label-keyword">Includes:</span> Table, Text, Photo, Signature, Product Screenshot
              </p>
            </div>

            <LabelExample
              id="table"
              title="Table"
              definition="Structured data organized into rows and columns."
              examples={[
                'Financial statements rendered as images',
                'Comparison tables with headers and cells'
              ]}
              rules={[
                'A visible grid or aligned rows/columns must be present',
                'Label as a table even if it includes small charts or icons',
                'Do not confuse with infographics lacking strict alignment'
              ]}
              imgsMeta={[
                { src: `${publicRoot}/table/image-1770404210749.png`, caption: 'Table 1' },
                { src: `${publicRoot}/table/image-1770404220036.png`, caption: 'Table 2' },
                { src: `${publicRoot}/table/image-1770404262055.png`, caption: 'Table 3' },
                { src: `${publicRoot}/table/image-1770404271954.png`, caption: 'Table 4' },
                { src: `${publicRoot}/table/image-1770404350188.png`, caption: 'Table 5' }
              ]}
            />

            <LabelExample
              id="text"
              title="Text"
              definition="Images dominated by textual content intended to be read verbatim."
              examples={[
                'Scanned letters or signed statements',
                'Screenshots of document pages',
                'Images with paragraphs or bullet points'
              ]}
              rules={[
                'Text must be the primary carrier of information',
                'Exclude captions or axis labels',
                'If text is secondary, consider Infographic'
              ]}
              imgsMeta={[
                { src: `${publicRoot}/text/image-1770404641905.png`, caption: 'Text 1' },
                { src: `${publicRoot}/text/image-1770404670605.png`, caption: 'Text 2' },
                { src: `${publicRoot}/text/image-1770404686732.png`, caption: 'Text 3' }
              ]}
            />

            <LabelExample
              id="photo"
              title="Photo"
              definition="Naturalistic photographs depicting real-world scenes or objects."
              examples={[
                'Photos of executives or teams',
                'Buildings or facilities',
                'Physical product photographs'
              ]}
              rules={[
                'Must appear photographic, not illustrated',
                'Exclude screenshots of software interfaces',
                'If heavily annotated, consider Infographic'
              ]}
              imgsMeta={[
                { src: `${publicRoot}/photo/image-1770404904186.png`, caption: 'Photo 1' },
                { src: `${publicRoot}/photo/image-1770404923433.png`, caption: 'Photo 2' },
                { src: `${publicRoot}/photo/image-1770404941094.png`, caption: 'Photo 3' },
                { src: `${publicRoot}/photo/image-1770404977615.png`, caption: 'Photo 4' }
              ]}
            />

            <LabelExample
              id="signature"
              title="Signature"
              definition="Images primarily showing handwritten or stylized signatures."
              examples={[
                'Executive signatures'
              ]}
              rules={[
                'Signature must be visually dominant',
                'Ignore surrounding text unless overwhelming',
                'If embedded in a page, label as Text'
              ]}
              imgsMeta={[
                { src: `${publicRoot}/signature/image-1770405068291.png`, caption: 'Signature 1' },
                { src: `${publicRoot}/signature/image-1770405131059.png`, caption: 'Signature 2' },
                { src: `${publicRoot}/signature/image-1770405197312.png`, caption: 'Signature 3' }
              ]}
            />

            <LabelExample
              id="screenshot"
              title="Product Screenshot"
              definition="Screenshots of digital products or software interfaces."
              examples={[
                'Web or mobile applications',
                'Dashboards or analytics tools'
              ]}
              rules={[
                'If text dominates without UI elements, label as Text'
              ]}
              imgsMeta={[
                { src: `${publicRoot}/product_screenshot/image-1770405249855.png`, caption: 'Product Screenshot 1' },
                { src: `${publicRoot}/product_screenshot/image-1770405274327.png`, caption: 'Product Screenshot 2' },
                { src: `${publicRoot}/product_screenshot/image-1770405287512.png`, caption: 'Product Screenshot 3' },
                { src: `${publicRoot}/product_screenshot/image-1770405338962.png`, caption: 'Product Screenshot 4' },
                { src: `${publicRoot}/product_screenshot/image-1770405430100.png`, caption: 'Product Screenshot 5' }
              ]}
            />
          </section>
        </main>
      </article>
    </div>
  );
}

export default AnnotationGuide;
